import React, { useMemo, useState } from "react";
import { Button, Checkbox, Input, Progress, Space, Table } from "antd";
import { FilterFilled, SearchOutlined } from "@ant-design/icons";
import {
  compareNumber,
  compareText,
  cumplimientoFilters,
  formatCumplimiento,
  getSemaforoCumplimiento,
  matchesCumplimientoRanges,
  matchesTextSearch,
  normalizePercent,
  semaforoFilters,
  semaforoOrder,
  semaforoColors,
} from "../complementosUtils";
import "../complementos.css";

const renderCumplimiento = (value) => (
  <Progress
    percent={normalizePercent(value)}
    size="small"
    format={() => formatCumplimiento(value)}
  />
);

const renderSemaforo = (semaforo) => (
  <div style={{ textAlign: "center" }} title={semaforo}>
    <span
      className="complementos-semaforo-pulse"
      style={{ "--complementos-semaforo-color": semaforoColors[semaforo] || semaforoColors.Gris }}
    />
  </div>
);

const optionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const checkboxStyle = {
  marginLeft: 0,
};

const renderTextFilter = ({ value, placeholder, onChange, onReset }) => (
  <div style={{ padding: 8, width: 220 }}>
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Input
        allowClear
        autoFocus
        placeholder={placeholder}
        prefix={<SearchOutlined />}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <Button size="small" onClick={onReset} disabled={!value}>
        Reset
      </Button>
    </Space>
  </div>
);

const renderCheckboxFilter = ({ value, options, onChange, onReset }) => (
  <div style={{ padding: 8, width: 190 }}>
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Checkbox.Group value={value} onChange={onChange}>
        <div style={optionStyle}>
          {options.map((option) => (
            <Checkbox key={option.value} value={option.value} style={checkboxStyle}>
              {option.text}
            </Checkbox>
          ))}
        </div>
      </Checkbox.Group>
      <Button size="small" onClick={onReset} disabled={!value.length}>
        Reset
      </Button>
    </Space>
  </div>
);

export default function TablaProveedoresComplementos({ dataSource, loading }) {
  const [proveedorFiltro, setProveedorFiltro] = useState("");
  const [semaforoFiltro, setSemaforoFiltro] = useState([]);
  const [cumplimientoFiltro, setCumplimientoFiltro] = useState([]);

  const filteredData = useMemo(() => {
    return (dataSource || []).filter((record) => {
      const semaforo = getSemaforoCumplimiento(record);

      return (
        matchesTextSearch(proveedorFiltro, record.proveedor) &&
        (!semaforoFiltro.length || semaforoFiltro.includes(semaforo)) &&
        matchesCumplimientoRanges(cumplimientoFiltro, record)
      );
    });
  }, [cumplimientoFiltro, dataSource, proveedorFiltro, semaforoFiltro]);

  const columns = [
    {
      title: "Semaforo",
      key: "semaforo",
      width: 110,
      filteredValue: semaforoFiltro,
      filterIcon: () => <FilterFilled style={{ color: semaforoFiltro.length ? "#1890ff" : undefined }} />,
      filterDropdown: () => renderCheckboxFilter({
        value: semaforoFiltro,
        options: semaforoFilters,
        onChange: setSemaforoFiltro,
        onReset: () => setSemaforoFiltro([]),
      }),
      sorter: (a, b) => {
        const semaforoA = getSemaforoCumplimiento(a);
        const semaforoB = getSemaforoCumplimiento(b);
        return (semaforoOrder[semaforoA] || 0) - (semaforoOrder[semaforoB] || 0);
      },
      render: (_, record) => {
        const semaforo = getSemaforoCumplimiento(record);
        return renderSemaforo(semaforo);
      },
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor",
      key: "proveedor",
      width: 240,
      filteredValue: proveedorFiltro ? [proveedorFiltro] : null,
      filterIcon: () => <SearchOutlined style={{ color: proveedorFiltro ? "#1890ff" : undefined }} />,
      filterDropdown: () => renderTextFilter({
        value: proveedorFiltro,
        placeholder: "Buscar proveedor",
        onChange: setProveedorFiltro,
        onReset: () => setProveedorFiltro(""),
      }),
      sorter: (a, b) => compareText(a, b, "proveedor"),
    },
    {
      title: "Facturas pagadas",
      dataIndex: "facturasPagadas",
      key: "facturasPagadas",
      width: 140,
      align: "center",
      sorter: (a, b) => compareNumber(a, b, "facturasPagadas"),
    },
    {
      title: "Con complemento pago",
      dataIndex: "conComplementoPago",
      key: "conComplementoPago",
      width: 170,
      align: "center",
      sorter: (a, b) => compareNumber(a, b, "conComplementoPago"),
    },
    {
      title: "Pendientes",
      dataIndex: "diferencia",
      key: "diferencia",
      width: 105,
      align: "center",
      sorter: (a, b) => compareNumber(a, b, "diferencia"),
    },
    {
      title: "Cumplimiento",
      dataIndex: "porcentajeCumplimiento",
      key: "porcentajeCumplimiento",
      width: 150,
      filteredValue: cumplimientoFiltro,
      filterIcon: () => <FilterFilled style={{ color: cumplimientoFiltro.length ? "#1890ff" : undefined }} />,
      filterDropdown: () => renderCheckboxFilter({
        value: cumplimientoFiltro,
        options: cumplimientoFilters,
        onChange: setCumplimientoFiltro,
        onReset: () => setCumplimientoFiltro([]),
      }),
      sorter: (a, b) => compareNumber(a, b, "porcentajeCumplimiento"),
      render: renderCumplimiento,
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      scroll={{ x: 900 }}
    />
  );
}
